import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, AlertTriangle, Bell, Shield, FileText, TrendingUp, Mail, Building2, Newspaper, CheckCircle } from 'lucide-react';
import SeoHead from '../seo/SeoHead';
import { staticPageMeta } from '../seo/metadata';
import { breadcrumbSchema } from '../seo/structuredData';
import { trackAlertSignup } from '../seo/analytics';

const INTER_STACK = "'Inter', system-ui, -apple-system, sans-serif";

// Space Mono wordmark — matches the SEC Trace tile on the Northlight portfolio.
const SECTRACE_WORDMARK = {
  fontFamily: "'Space Mono', 'Courier New', monospace",
  fontWeight: 700,
  letterSpacing: '-0.03em'
};

const SecTraceLandingPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [recentB7A, setRecentB7A] = useState([]);
  const [topOrganizations, setTopOrganizations] = useState([]);
  // Sensible hardcoded fallbacks so the hero never shows 'Loading...' if the
  // backend stats endpoint is slow or fails. Live values overwrite these the
  // moment the /api/sectrace/stats call resolves successfully.
  const [stats, setStats] = useState({
    total_requests: '250,000+',
    total_b7a_exemptions: '8,500+',
    years_of_data: '20+'
  });
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [searchError, setSearchError] = useState(null);

  const [alertEmail, setAlertEmail] = useState('');
  const [alertCompanies, setAlertCompanies] = useState('');
  const [selectedAlertType, setSelectedAlertType] = useState('portfolio');
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [subscribeSuccess, setSubscribeSuccess] = useState(false);
  const [subscribeError, setSubscribeError] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await fetch('/api/sectrace/stats');
        if (!res.ok) return;
        const data = await res.json();
        const safeNum = (v, fallback) => {
          const n = Number(v);
          return Number.isFinite(n) && n > 0 ? n.toLocaleString() + '+' : fallback;
        };
        const safeYears = (v, fallback) => {
          const n = Number(v);
          return Number.isFinite(n) && n > 0 ? `${n}+` : fallback;
        };
        setStats((prev) => ({
          total_requests: safeNum(data.total_requests, prev.total_requests),
          total_b7a_exemptions: safeNum(data.total_b7a_exemptions, prev.total_b7a_exemptions),
          years_of_data: safeYears(data.years_of_data, prev.years_of_data)
        }));
      } catch (e) {
        console.error('Stats fetch error:', e);
      }
    };

    const fetchB7A = async () => {
      try {
        const res = await fetch('/api/sectrace/recent-b7a?limit=10');
        if (res.ok) {
          const data = await res.json();
          setRecentB7A(data);
        }
      } catch (e) {
        console.error('B7A fetch error:', e);
      }
    };

    const fetchOrgs = async () => {
      try {
        const res = await fetch('/api/sectrace/top-organizations?limit=20');
        if (res.ok) {
          const data = await res.json();
          setTopOrganizations(data);
        }
      } catch (e) {
        console.error('Orgs fetch error:', e);
      }
    };

    fetchStats();
    fetchB7A();
    fetchOrgs();
  }, []);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (searchTerm.length < 2) return;

    setIsSearching(true);
    setHasSearched(true);
    setSearchError(null);

    try {
      const response = await fetch(`/api/sectrace/search?q=${encodeURIComponent(searchTerm)}&b7a_only=true`);
      const data = await response.json();

      if (response.ok && Array.isArray(data)) {
        setSearchResults(data.map((item) => ({
          id: item.request_id || item.id,
          request_id: item.request_id,
          description: item.request_description,
          date: item.received_date,
          requester: item.requester_name,
          organization: item.organization,
          disposition: item.final_disposition,
          b7a: item.is_b7a
        })));
      } else {
        setSearchError('Search failed. Please try again.');
        setSearchResults([]);
      }
    } catch (error) {
      setSearchError('Connection error. Please try again.');
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!alertEmail || !alertEmail.includes('@')) {
      setSubscribeError('Please enter a valid email address.');
      return;
    }

    setIsSubscribing(true);
    setSubscribeError(null);

    try {
      const response = await fetch('/api/sectrace/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: alertEmail,
          companies: alertCompanies.split(',').map((c) => c.trim()).filter((c) => c),
          alert_type: selectedAlertType
        })
      });

      if (response.ok) {
        setSubscribeSuccess(true);
        trackAlertSignup({ source: 'sectrace_landing', subject: selectedAlertType });
        setAlertEmail('');
        setAlertCompanies('');
      } else {
        setSubscribeError('Failed to subscribe. Please try again.');
      }
    } catch (error) {
      setSubscribeError('Connection error. Please try again.');
    } finally {
      setIsSubscribing(false);
    }
  };

  const alertTypes = [
    {
      id: 'portfolio',
      icon: Shield,
      title: 'Portfolio Protection',
      description: 'Get alerts on SEC investigations or journalistic inquiries on your entire portfolio. We search the entire SEC FOIA Logs as they become public for any FOIA requests or B7A investigation exemptions on your portfolio companies. Track up to 50 companies with one monthly email alert.',
      inputLabel: 'Enter company names (comma-separated)',
      inputHint: 'Use short names: "Disney" not "The Walt Disney Company"'
    },
    {
      id: 'ir_watch',
      icon: Building2,
      title: 'IR Watch',
      description: 'Get alerts when hedge funds, research firms, or reporters file FOIA requests with the SEC on your company. Designed for investor relations professionals.',
      inputLabel: 'Enter your company name',
      inputHint: 'Use short name: "Tesla" not "Tesla, Inc."'
    },
    {
      id: 'reporter',
      icon: Newspaper,
      title: 'Prominent Reporter Alerts',
      description: 'See what journalists are working on before the stories are published with our monthly roundup of SEC FOIA requests from prominent reporters.',
      inputLabel: null,
      inputHint: null
    }
  ];

  const features = [
    {
      icon: Search,
      title: 'Company Search',
      description: 'Search B7A exemptions to find companies with potential SEC investigations.'
    },
    {
      icon: AlertTriangle,
      title: 'B7A Detection',
      description: 'Identify B7A exemptions that indicate potential undisclosed SEC investigations.'
    },
    {
      icon: Shield,
      title: 'Portfolio Protection',
      description: 'Get alerts when SEC investigations or journalistic inquiries target your holdings.'
    },
    {
      icon: Bell,
      title: 'IR Watch',
      description: 'Know when hedge funds, research firms, or reporters file FOIA requests on your company.'
    },
    {
      icon: FileText,
      title: 'Reporter Tracking',
      description: 'See what journalists are investigating before stories are published.'
    },
    {
      icon: TrendingUp,
      title: 'Real-time Updates',
      description: 'Daily updates from SEC FOIA logs with instant alerts on new activity.'
    }
  ];

  const displayStats = [
    { label: 'FOIA Requests Tracked', value: stats.total_requests },
    { label: 'B7A Exemptions Found', value: stats.total_b7a_exemptions },
    { label: 'Years of Data', value: stats.years_of_data }
  ];

  // Shared typographic tokens — identical treatment to Phalanx / Sovra.
  const headingStyle = {
    fontFamily: INTER_STACK,
    fontWeight: 200,
    letterSpacing: '-0.02em'
  };
  const bodyStyle = {
    fontFamily: INTER_STACK,
    fontWeight: 300
  };
  const ctaStyle = {
    fontFamily: INTER_STACK,
    fontWeight: 500,
    letterSpacing: '0.18em',
    fontSize: '0.7rem'
  };

  const labelStyle = {
    ...bodyStyle,
    fontSize: '0.65rem',
    letterSpacing: '0.2em'
  };

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: INTER_STACK }}>
      <SeoHead
        {...staticPageMeta({
          title: 'SEC Trace | SEC FOIA Investigation Tracker',
          description: 'SEC Trace: SEC FOIA investigation tracker.',
          path: '/sectrace',
          ogImage: `${process.env.REACT_APP_SITE_URL || 'https://researchphalanx.com'}/sectrace-logo.png`,
        })}
        jsonLd={breadcrumbSchema([
          { name: 'Phalanx', path: '/' },
          { name: 'SEC Trace', path: '/sectrace' },
        ])}
      />
      {/* Header — transparent, floats over the hero. Wordmark in Space Mono
          to mirror the SEC Trace tile on the Northlight portfolio. */}
      <header className="absolute top-0 left-0 right-0 z-30 px-6">
        <div className="max-w-6xl mx-auto py-5 sm:py-7">
          <nav className="flex items-center justify-between gap-4">
            <div className="flex items-baseline gap-5 sm:gap-6">
              <span
                className="text-white leading-none"
                style={{
                  ...SECTRACE_WORDMARK,
                  fontSize: 'clamp(1.25rem, 3vw, 2.25rem)',
                  textShadow: '0 2px 20px rgba(0,0,0,0.3)'
                }}
              >
                SEC Trace
              </span>
            </div>
            <div
              className="flex items-center gap-4 sm:gap-6"
              style={{
                ...bodyStyle,
                fontSize: '0.85rem',
                letterSpacing: '-0.01em',
                textShadow: '0 2px 20px rgba(0,0,0,0.3)'
              }}
            >
              <a href="#search" className="text-white/70 hover:text-white hidden sm:inline transition-colors">Search</a>
              <a href="#alerts" className="text-white/70 hover:text-white hidden sm:inline transition-colors">Alerts</a>
              <Link to="/sectrace/research" className="text-white hover:text-white/70 transition-colors">Research</Link>
            </div>
          </nav>
        </div>
      </header>

      {/* Hero — full viewport dark panel with subtle gradient and stats overlaid */}
      <section className="relative h-screen overflow-hidden bg-gray-900">
        {/* Soft radial glow for depth */}
        <div
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(ellipse at 50% 30%, rgba(60,64,72,0.55) 0%, rgba(17,24,39,0.25) 45%, rgba(0,0,0,0.6) 100%)'
          }}
        />
        {/* Faint dot grid for "data" texture */}
        <div
          className="absolute inset-0 opacity-[0.08]"
          style={{
            backgroundImage:
              'radial-gradient(rgba(255,255,255,0.9) 1px, transparent 1px)',
            backgroundSize: '28px 28px'
          }}
        />

        <div className="relative h-full flex flex-col">
          {/* Centered headline + subcopy */}
          <div className="flex-1 flex items-center justify-center px-6">
            <div className="text-center max-w-4xl">
              <h1
                className="text-white mb-6 leading-[1.15]"
                style={{
                  ...headingStyle,
                  fontSize: 'clamp(1.75rem, 4.2vw, 3rem)',
                  textShadow: '0 2px 20px rgba(0,0,0,0.3)'
                }}
              >
                Uncover SEC Investigations
                <br />
                <span className="text-white/55">Before They&rsquo;re Public.</span>
              </h1>
              <p
                className="text-white/80 leading-relaxed max-w-2xl mx-auto"
                style={{
                  ...bodyStyle,
                  fontSize: 'clamp(0.95rem, 1.6vw, 1.15rem)',
                  textShadow: '0 1px 12px rgba(0,0,0,0.3)'
                }}
              >
                Track SEC FOIA requests in real time. Identify potential investigations through B7A exemptions. Protect your portfolio with early warnings.
              </p>
              <div className="flex flex-wrap items-center justify-center gap-6 sm:gap-8 mt-8 sm:mt-10">
                <a
                  href="#search"
                  className="inline-flex items-center gap-2 text-white border-b border-white hover:border-gray-300 hover:text-gray-300 pb-0.5 uppercase transition-colors"
                  style={ctaStyle}
                >
                  Search B7A Exemptions
                  <span className="text-base">→</span>
                </a>
                <a
                  href="#alerts"
                  className="inline-flex items-center gap-2 text-white/70 border-b border-white/40 hover:border-white hover:text-white pb-0.5 uppercase transition-colors"
                  style={ctaStyle}
                >
                  Set Up Alerts
                  <span className="text-base">→</span>
                </a>
              </div>
            </div>
          </div>

          {/* Stats row at the bottom of the hero */}
          <div className="pb-10 sm:pb-14 px-6">
            <div className="max-w-5xl mx-auto grid grid-cols-3 gap-3 sm:gap-8 text-center">
              {displayStats.map((stat) => (
                <div key={stat.label} className="px-2 sm:px-4">
                  <div
                    className="text-white mb-1.5 sm:mb-2"
                    style={{
                      ...headingStyle,
                      fontSize: 'clamp(1.5rem, 3.6vw, 2.75rem)',
                      textShadow: '0 2px 20px rgba(0,0,0,0.35)'
                    }}
                  >
                    {stat.value}
                  </div>
                  <div
                    className="text-white/60 uppercase"
                    style={{
                      ...bodyStyle,
                      fontSize: '0.65rem',
                      letterSpacing: '0.2em'
                    }}
                  >
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Search Section */}
      <section id="search" className="py-16 sm:py-24 px-6 bg-white border-b border-gray-100">
        <div className="max-w-4xl mx-auto">
          <div className="mb-10 sm:mb-12 max-w-2xl">
            <div className="uppercase text-gray-500 mb-3" style={labelStyle}>
              Search
            </div>
            <h2
              className="text-gray-900 leading-[1.2] mb-3"
              style={{ ...headingStyle, fontSize: 'clamp(1.5rem, 2.6vw, 2.25rem)' }}
            >
              Search B7A exemptions.
            </h2>
            <p className="text-gray-600 leading-relaxed" style={{ ...bodyStyle, fontSize: '1rem' }}>
              Enter a company name to find B7A exemptions indicating potential SEC investigations.
            </p>
          </div>

          <div className="rounded-xl border border-gray-200 bg-[#FAF8F3] p-5 sm:p-6 mb-6">
            <form onSubmit={handleSearch}>
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder='Search B7A exemptions (e.g. Disney, Tesla, Meta)...'
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-28 py-3 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  style={{ ...bodyStyle, fontWeight: 400 }}
                />
                <button
                  type="submit"
                  disabled={isSearching || searchTerm.length < 2}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 px-4 py-1.5 bg-gray-900 text-white text-sm rounded hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  style={{ ...bodyStyle, fontWeight: 500 }}
                >
                  {isSearching ? 'Searching…' : 'Search'}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-3" style={bodyStyle}>
                Use short company names for best results (e.g. &ldquo;Disney&rdquo; not &ldquo;The Walt Disney Company&rdquo;).
              </p>
            </form>
          </div>

          {hasSearched && (
            <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
              <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between bg-[#FAF8F3]">
                <span className="text-gray-700" style={{ ...bodyStyle, fontSize: '0.85rem' }}>
                  {searchError ? 'Error' : `Results for "${searchTerm}"`}
                </span>
                {!searchError && (
                  <span
                    className={`px-2 py-0.5 rounded ${
                      searchResults.length > 20
                        ? 'bg-red-100 text-red-700'
                        : searchResults.length > 5
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-gray-100 text-gray-700'
                    }`}
                    style={{ ...bodyStyle, fontWeight: 500, fontSize: '0.75rem' }}
                  >
                    {searchResults.length} FOIA {searchResults.length === 1 ? 'request' : 'requests'}
                  </span>
                )}
              </div>
              {searchError ? (
                <div className="px-5 py-8 text-center text-red-600" style={{ ...bodyStyle, fontSize: '0.9rem' }}>
                  {searchError}
                </div>
              ) : searchResults.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead>
                      <tr className="bg-white border-b border-gray-100">
                        <th
                          className="px-5 py-3 text-left uppercase text-gray-500 w-24"
                          style={labelStyle}
                        >
                          Date
                        </th>
                        <th
                          className="px-5 py-3 text-left uppercase text-gray-500"
                          style={labelStyle}
                        >
                          Request Description
                        </th>
                        <th
                          className="px-5 py-3 text-left uppercase text-gray-500 w-40"
                          style={labelStyle}
                        >
                          Requester
                        </th>
                        <th
                          className="px-5 py-3 text-left uppercase text-gray-500 w-16"
                          style={labelStyle}
                        >
                          B7A
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {searchResults.map((result) => (
                        <tr key={result.id} className={`hover:bg-gray-50 ${result.b7a ? 'bg-red-50/30' : ''}`}>
                          <td className="px-5 py-3 text-gray-500 whitespace-nowrap" style={{ ...bodyStyle, fontSize: '0.85rem' }}>
                            {formatDate(result.date)}
                          </td>
                          <td className="px-5 py-3">
                            <div className="text-gray-900 line-clamp-2" style={{ ...bodyStyle, fontWeight: 400, fontSize: '0.875rem' }}>
                              {result.description}
                            </div>
                            <div className="text-gray-400 mt-0.5" style={{ ...bodyStyle, fontSize: '0.7rem' }}>
                              {result.request_id}
                            </div>
                          </td>
                          <td className="px-5 py-3">
                            <div className="text-gray-700" style={{ ...bodyStyle, fontSize: '0.85rem' }}>{result.requester}</div>
                            {result.organization && result.organization !== '-' && (
                              <div className="text-gray-400" style={{ ...bodyStyle, fontSize: '0.7rem' }}>
                                {result.organization}
                              </div>
                            )}
                          </td>
                          <td className="px-5 py-3">
                            {result.b7a ? (
                              <span
                                className="inline-flex items-center gap-1 text-red-700 bg-red-100 px-2 py-0.5 rounded"
                                style={{ ...bodyStyle, fontWeight: 500, fontSize: '0.7rem' }}
                              >
                                <AlertTriangle className="w-3 h-3" /> B7A
                              </span>
                            ) : (
                              <span className="text-gray-400" style={{ ...bodyStyle, fontSize: '0.7rem' }}>-</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : !searchError ? (
                <div className="px-5 py-8 text-center text-gray-500" style={{ ...bodyStyle, fontSize: '0.9rem' }}>
                  No results found. Try a different search term.
                </div>
              ) : null}
              <div className="px-5 py-3 border-t border-gray-100 bg-[#FAF8F3] text-center">
                <span className="text-gray-500" style={{ ...bodyStyle, fontSize: '0.75rem' }}>
                  Login for full access to all FOIA requests and alerts.
                </span>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Recent B7A Exemptions */}
      {recentB7A.length > 0 && (
        <section className="py-16 sm:py-24 px-6 bg-white border-b border-gray-100">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
              <div className="max-w-2xl">
                <div className="uppercase text-gray-500 mb-3" style={labelStyle}>
                  Live
                </div>
                <h2
                  className="text-gray-900 leading-[1.2] mb-3"
                  style={{ ...headingStyle, fontSize: 'clamp(1.5rem, 2.6vw, 2.25rem)' }}
                >
                  Recent B7A exemptions.
                </h2>
                <p className="text-gray-600 leading-relaxed" style={{ ...bodyStyle, fontSize: '0.95rem' }}>
                  Latest FOIA requests denied due to potential ongoing investigations.
                </p>
              </div>
            </div>

            <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className="bg-[#FAF8F3] border-b border-gray-100">
                      <th
                        className="px-5 py-3 text-left uppercase text-gray-500 w-24"
                        style={labelStyle}
                      >
                        Date
                      </th>
                      <th
                        className="px-5 py-3 text-left uppercase text-gray-500"
                        style={labelStyle}
                      >
                        Company / Subject
                      </th>
                      <th
                        className="px-5 py-3 text-center uppercase text-gray-500 w-28"
                        style={labelStyle}
                      >
                        FOIA Mentions
                      </th>
                      <th
                        className="px-5 py-3 text-left uppercase text-gray-500 w-36"
                        style={labelStyle}
                      >
                        Requester
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {recentB7A.map((item) => (
                      <tr key={item.request_id} className="hover:bg-gray-50">
                        <td className="px-5 py-3 text-gray-500 whitespace-nowrap" style={{ ...bodyStyle, fontSize: '0.85rem' }}>
                          {formatDate(item.received_date)}
                        </td>
                        <td className="px-5 py-3">
                          <div className="flex items-start gap-2">
                            <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                            <div>
                              <div className="text-gray-900" style={{ ...bodyStyle, fontWeight: 500, fontSize: '0.875rem' }}>
                                {item.company_name || item.request_description?.slice(0, 50) || '-'}
                              </div>
                              <div className="text-gray-400 line-clamp-1" style={{ ...bodyStyle, fontSize: '0.75rem' }}>
                                {item.request_description}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-3 text-center">
                          {item.previous_mentions > 0 ? (
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full ${
                                item.previous_mentions >= 10
                                  ? 'bg-red-100 text-red-800'
                                  : item.previous_mentions >= 5
                                    ? 'bg-orange-100 text-orange-800'
                                    : 'bg-yellow-100 text-yellow-800'
                              }`}
                              style={{ ...bodyStyle, fontWeight: 500, fontSize: '0.72rem' }}
                            >
                              {item.previous_mentions} FOIA
                            </span>
                          ) : (
                            <span className="text-gray-400" style={{ ...bodyStyle, fontSize: '0.72rem' }}>
                              First time
                            </span>
                          )}
                        </td>
                        <td className="px-5 py-3">
                          <div className="text-gray-700 truncate max-w-[140px]" style={{ ...bodyStyle, fontSize: '0.85rem' }}>
                            {item.requester_name}
                          </div>
                          {item.organization && item.organization !== '-' && (
                            <div className="text-gray-400 truncate max-w-[140px]" style={{ ...bodyStyle, fontSize: '0.7rem' }}>
                              {item.organization}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <p className="text-gray-400 mt-4 text-center" style={{ ...bodyStyle, fontSize: '0.72rem' }}>
              &ldquo;FOIA Mentions&rdquo; shows total FOIA requests (not just B7A) where this company was mentioned.
            </p>
          </div>
        </section>
      )}

      {/* Top Organizations */}
      {topOrganizations.length > 0 && (
        <section className="py-16 sm:py-24 px-6 bg-white border-b border-gray-100">
          <div className="max-w-5xl mx-auto">
            <div className="mb-10 max-w-2xl">
              <div className="uppercase text-gray-500 mb-3" style={labelStyle}>
                Requesters
              </div>
              <h2
                className="text-gray-900 leading-[1.2] mb-3"
                style={{ ...headingStyle, fontSize: 'clamp(1.5rem, 2.6vw, 2.25rem)' }}
              >
                Who&rsquo;s requesting SEC records?
              </h2>
              <p className="text-gray-600 leading-relaxed" style={{ ...bodyStyle, fontSize: '0.95rem' }}>
                Hedge funds, law firms, and research organizations filing the most FOIA requests.
              </p>
            </div>

            <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-gray-100">
                {[0, 1, 2].map((colIndex) => (
                  <div key={colIndex} className="divide-y divide-gray-100">
                    {topOrganizations
                      .slice(colIndex * Math.ceil(topOrganizations.length / 3), (colIndex + 1) * Math.ceil(topOrganizations.length / 3))
                      .map((org, index) => {
                        const actualIndex = colIndex * Math.ceil(topOrganizations.length / 3) + index;
                        return (
                          <div
                            key={org.organization}
                            className="flex items-center justify-between px-5 py-3 hover:bg-gray-50 transition-colors"
                          >
                            <div className="flex items-center gap-3 min-w-0">
                              <span
                                className={`w-5 ${actualIndex < 3 ? 'text-gray-900' : 'text-gray-400'}`}
                                style={{ ...bodyStyle, fontWeight: 500, fontSize: '0.75rem' }}
                              >
                                {actualIndex + 1}.
                              </span>
                              <span
                                className={`truncate ${actualIndex < 3 ? 'text-gray-900' : 'text-gray-700'}`}
                                style={{ ...bodyStyle, fontWeight: actualIndex < 3 ? 500 : 400, fontSize: '0.875rem' }}
                              >
                                {org.organization}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <span className="text-gray-500" style={{ ...bodyStyle, fontSize: '0.85rem' }}>
                                {parseInt(org.request_count).toLocaleString()}
                              </span>
                              {parseInt(org.b7a_count) > 0 && (
                                <span
                                  className="text-red-600 bg-red-50 px-1.5 py-0.5 rounded"
                                  style={{ ...bodyStyle, fontWeight: 500, fontSize: '0.7rem' }}
                                >
                                  {org.b7a_count}
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                ))}
              </div>
            </div>
            <p className="text-gray-400 mt-4 text-center" style={{ ...bodyStyle, fontSize: '0.72rem' }}>
              Request counts from SEC FOIA logs. B7A badges indicate requests denied due to ongoing investigations.
            </p>
          </div>
        </section>
      )}

      {/* Features — six tiles that invert on hover */}
      <section id="features" className="py-16 sm:py-24 px-6 bg-white border-b border-gray-100">
        <div className="max-w-5xl mx-auto">
          <div className="mb-10 sm:mb-14 max-w-2xl">
            <div className="uppercase text-gray-500 mb-3" style={labelStyle}>
              Features
            </div>
            <h2
              className="text-gray-900 leading-[1.2]"
              style={{ ...headingStyle, fontSize: 'clamp(1.5rem, 2.6vw, 2.25rem)' }}
            >
              Comprehensive FOIA monitoring and alerting.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-5">
            {features.map((f) => (
              <div
                key={f.title}
                className="group rounded-xl border border-gray-200 bg-[#FAF8F3] hover:bg-gray-900 hover:border-gray-900 p-6 sm:p-7 cursor-pointer"
                style={{
                  transition:
                    'background-color 700ms cubic-bezier(0.22, 1, 0.36, 1), border-color 700ms cubic-bezier(0.22, 1, 0.36, 1), box-shadow 700ms cubic-bezier(0.22, 1, 0.36, 1)'
                }}
              >
                <div
                  className="w-11 h-11 mb-5 flex items-center justify-center rounded-lg bg-white border border-gray-200 text-gray-700 group-hover:bg-gray-800 group-hover:border-gray-700 group-hover:text-white"
                  style={{
                    transition:
                      'background-color 700ms cubic-bezier(0.22, 1, 0.36, 1), border-color 700ms cubic-bezier(0.22, 1, 0.36, 1), color 700ms cubic-bezier(0.22, 1, 0.36, 1)'
                  }}
                >
                  <f.icon className="w-5 h-5" />
                </div>
                <h3
                  className="text-gray-900 group-hover:text-white mb-2 leading-snug"
                  style={{
                    ...headingStyle,
                    fontWeight: 400,
                    fontSize: '1.05rem',
                    letterSpacing: '-0.01em',
                    transition: 'color 700ms cubic-bezier(0.22, 1, 0.36, 1)'
                  }}
                >
                  {f.title}
                </h3>
                <p
                  className="text-gray-600 group-hover:text-gray-300 leading-relaxed"
                  style={{
                    ...bodyStyle,
                    fontSize: '0.875rem',
                    transition: 'color 700ms cubic-bezier(0.22, 1, 0.36, 1)'
                  }}
                >
                  {f.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Alert Services */}
      <section id="alerts" className="py-16 sm:py-24 px-6 bg-white border-b border-gray-100">
        <div className="max-w-4xl mx-auto">
          <div className="mb-10 sm:mb-14 max-w-2xl">
            <div className="uppercase text-gray-500 mb-3" style={labelStyle}>
              Alerts
            </div>
            <h2
              className="text-gray-900 leading-[1.2]"
              style={{ ...headingStyle, fontSize: 'clamp(1.5rem, 2.6vw, 2.25rem)' }}
            >
              Automated alerts on SEC FOIA activity.
            </h2>
          </div>

          {/* Alert type selection */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-5 mb-8">
            {alertTypes.map((type) => {
              const active = selectedAlertType === type.id;
              return (
                <button
                  key={type.id}
                  onClick={() => {
                    setSelectedAlertType(type.id);
                    setSubscribeSuccess(false);
                  }}
                  className={`rounded-xl border p-5 sm:p-6 text-left cursor-pointer ${
                    active
                      ? 'bg-gray-900 border-gray-900'
                      : 'bg-[#FAF8F3] border-gray-200 hover:border-gray-300'
                  }`}
                  style={{
                    transition:
                      'background-color 500ms cubic-bezier(0.22, 1, 0.36, 1), border-color 500ms cubic-bezier(0.22, 1, 0.36, 1)'
                  }}
                >
                  <div
                    className={`w-10 h-10 mb-4 flex items-center justify-center rounded-lg border ${
                      active
                        ? 'bg-gray-800 border-gray-700 text-white'
                        : 'bg-white border-gray-200 text-gray-700'
                    }`}
                    style={{
                      transition:
                        'background-color 500ms cubic-bezier(0.22, 1, 0.36, 1), border-color 500ms cubic-bezier(0.22, 1, 0.36, 1), color 500ms cubic-bezier(0.22, 1, 0.36, 1)'
                    }}
                  >
                    <type.icon className="w-5 h-5" />
                  </div>
                  <h3
                    className={`mb-2 leading-snug ${active ? 'text-white' : 'text-gray-900'}`}
                    style={{ ...headingStyle, fontWeight: 400, fontSize: '1rem', letterSpacing: '-0.01em' }}
                  >
                    {type.title}
                  </h3>
                  <p
                    className={`leading-relaxed line-clamp-2 ${active ? 'text-gray-300' : 'text-gray-600'}`}
                    style={{ ...bodyStyle, fontSize: '0.8rem' }}
                  >
                    {type.description.substring(0, 90)}…
                  </p>
                </button>
              );
            })}
          </div>

          {/* Selected alert details + subscription form */}
          <div className="rounded-xl border border-gray-200 bg-white p-6 sm:p-8">
            {alertTypes.filter((t) => t.id === selectedAlertType).map((type) => (
              <div key={type.id}>
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#FAF8F3] border border-gray-200 text-gray-700 flex-shrink-0">
                    <type.icon className="w-5 h-5" />
                  </div>
                  <div>
                    <h3
                      className="text-gray-900 mb-2 leading-snug"
                      style={{ ...headingStyle, fontWeight: 400, fontSize: '1.1rem', letterSpacing: '-0.01em' }}
                    >
                      {type.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed" style={{ ...bodyStyle, fontSize: '0.9rem' }}>
                      {type.description}
                    </p>
                  </div>
                </div>

                {subscribeSuccess ? (
                  <div className="rounded-lg bg-green-50 border border-green-200 p-4 flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                    <div>
                      <p className="text-green-900" style={{ ...bodyStyle, fontWeight: 500, fontSize: '0.875rem' }}>
                        Successfully subscribed.
                      </p>
                      <p className="text-green-700" style={{ ...bodyStyle, fontSize: '0.75rem' }}>
                        You&rsquo;ll receive alerts at the email provided.
                      </p>
                    </div>
                  </div>
                ) : (
                  <form onSubmit={handleSubscribe} className="space-y-4">
                    <div>
                      <label
                        className="block text-gray-700 mb-2 uppercase"
                        style={{ ...bodyStyle, fontSize: '0.65rem', letterSpacing: '0.18em' }}
                      >
                        Email Address
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="email"
                          value={alertEmail}
                          onChange={(e) => setAlertEmail(e.target.value)}
                          placeholder="you@example.com"
                          className="w-full pl-10 pr-4 py-2.5 bg-[#FAF8F3] border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                          style={{ ...bodyStyle, fontWeight: 400 }}
                          required
                        />
                      </div>
                    </div>

                    {type.inputLabel && (
                      <div>
                        <label
                          className="block text-gray-700 mb-2 uppercase"
                          style={{ ...bodyStyle, fontSize: '0.65rem', letterSpacing: '0.18em' }}
                        >
                          {type.inputLabel}
                        </label>
                        <input
                          type="text"
                          value={alertCompanies}
                          onChange={(e) => setAlertCompanies(e.target.value)}
                          placeholder={type.id === 'ir_watch' ? 'e.g. Tesla' : 'e.g. Apple, Tesla, Meta, Disney'}
                          className="w-full px-4 py-2.5 bg-[#FAF8F3] border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                          style={{ ...bodyStyle, fontWeight: 400 }}
                        />
                        {type.inputHint && (
                          <p className="mt-2 text-gray-500" style={{ ...bodyStyle, fontSize: '0.75rem' }}>
                            {type.inputHint}
                          </p>
                        )}
                      </div>
                    )}

                    {subscribeError && (
                      <p className="text-red-600" style={{ ...bodyStyle, fontSize: '0.875rem' }}>
                        {subscribeError}
                      </p>
                    )}

                    <button
                      type="submit"
                      disabled={isSubscribing}
                      className="inline-flex items-center gap-2 text-white bg-gray-900 hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed px-5 py-2.5 rounded-lg uppercase transition-colors"
                      style={ctaStyle}
                    >
                      {isSubscribing ? 'Subscribing…' : 'Subscribe to Alerts'}
                      <span className="text-base">→</span>
                    </button>
                  </form>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* B7A Explanation */}
      <section className="py-16 sm:py-24 px-6 bg-white border-b border-gray-100">
        <div className="max-w-3xl mx-auto">
          <div className="rounded-xl border border-red-100 bg-red-50/60 p-6 sm:p-8">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-white border border-red-100 text-red-600 flex-shrink-0">
                <AlertTriangle className="w-5 h-5" />
              </div>
              <div>
                <div className="uppercase text-red-700 mb-3" style={labelStyle}>
                  What is a B7A Exemption?
                </div>
                <p className="text-red-900 leading-relaxed" style={{ ...bodyStyle, fontSize: '0.95rem' }}>
                  When the SEC denies a FOIA request citing &ldquo;Exemption 7(A)&rdquo;, it means releasing the information could interfere with an ongoing law enforcement proceeding. This is often a strong signal of an <strong>undisclosed SEC investigation</strong> into the company. B7A exemptions have preceded major enforcement actions and can be an early warning sign for investors.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 sm:py-24 px-6 bg-white">
        <div className="max-w-2xl mx-auto text-center">
          <h3
            className="text-gray-900 mb-5 leading-[1.2]"
            style={{ ...headingStyle, fontSize: 'clamp(1.5rem, 2.6vw, 2.25rem)' }}
          >
            Protect your portfolio.
          </h3>
          <p className="text-gray-600 mb-8 leading-relaxed" style={{ ...bodyStyle, fontSize: '1rem' }}>
            Get alerts when SEC investigations or journalistic inquiries target your holdings.
          </p>
          <a
            href="mailto:Contact@researchphalanx.com?subject=SEC%20Trace%20Access"
            className="inline-flex items-center gap-2 text-gray-900 border-b border-gray-900 hover:border-gray-500 hover:text-gray-500 pb-0.5 uppercase transition-colors"
            style={ctaStyle}
          >
            Request Access
            <span className="text-base">→</span>
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-200">
        <div
          className="max-w-5xl mx-auto px-6 py-6 sm:py-8 flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4 uppercase text-[0.6rem] sm:text-[0.68rem] tracking-[0.18em] sm:tracking-[0.28em]"
          style={{ fontFamily: INTER_STACK, fontWeight: 400 }}
        >
          <span
            className="text-gray-900 leading-none"
            style={{ ...SECTRACE_WORDMARK, fontSize: '1.1rem' }}
          >
            SEC Trace
          </span>
          <p className="text-gray-400 text-center">&copy; 2026 SEC Trace. A Northlight Technologies company.</p>
        </div>
      </footer>
    </div>
  );
};

export default SecTraceLandingPage;
